import { useEffect, useRef, useState, useCallback } from 'react';
import {
  getMatchIds,
  getMatchDetails,
  getMatchTimeline,
  parseMatchToApexGame
} from './riot';

/**
 * APEX v7.1 — Auto-Import Hook
 *
 * Lance un import Riot automatique toutes les N millisecondes (default 15min).
 * Aussi exposable manuellement via la fonction `triggerImport()`.
 *
 * Conditions d'activation :
 *   - userSettings.riot_puuid doit être défini
 *   - Pas déjà en cours
 *   - Pas en throttle (60s mini entre 2 imports manuels)
 *
 * Comportement :
 *   - Récupère les 5 derniers matchIds (queue 420 SoloQ par défaut)
 *   - Pour chaque match : check déduplication, fetch détails + timeline, parse, importe
 *   - Échec silencieux : on log mais on ne bloque pas la suite
 *   - Notifie via le callback onImported avec la liste des games ajoutées
 *
 * Returns:
 *   { isImporting, lastImportAt, triggerImport, lastResult }
 */

const DEFAULT_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const MANUAL_THROTTLE_MS = 60 * 1000;       // 1 minute entre 2 imports manuels
const MATCH_COUNT = 5;                       // 5 dernières games par tick

export function useAutoImport({
  userSettings,
  existingGames,
  importGame,
  intervalMs = DEFAULT_INTERVAL_MS,
  enabled = true,
  queueId = 420 // 420 = SoloQ ; null = tout
}) {
  const [isImporting, setIsImporting] = useState(false);
  const [lastImportAt, setLastImportAt] = useState(null);
  const [lastResult, setLastResult] = useState(null); // { added, skipped, errors }
  const lastManualRef = useRef(0);
  const intervalRef = useRef(null);
  const inFlightRef = useRef(false);

  const performImport = useCallback(async ({ manual = false } = {}) => {
    if (!userSettings?.riot_puuid) {
      const result = { added: 0, skipped: 0, errors: 0, reason: 'no_puuid' };
      setLastResult(result);
      return result;
    }
    if (inFlightRef.current) {
      return { added: 0, skipped: 0, errors: 0, reason: 'in_flight' };
    }
    if (manual && Date.now() - lastManualRef.current < MANUAL_THROTTLE_MS) {
      return { added: 0, skipped: 0, errors: 0, reason: 'throttled' };
    }

    inFlightRef.current = true;
    setIsImporting(true);
    let result = { added: 0, skipped: 0, errors: 0 };

    try {
      const platform = userSettings.riot_platform || 'europe';
      const matchIds = await getMatchIds(userSettings.riot_puuid, {
        count: MATCH_COUNT,
        queue: queueId,
        platform
      });

      // Filtrer matchIds déjà importés
      const existingIds = new Set(
        (existingGames || []).map(g => g.riot_match_id).filter(Boolean)
      );
      const newIds = (matchIds || []).filter(id => !existingIds.has(id));
      result.skipped = (matchIds?.length || 0) - newIds.length;

      // Fetch détails + timeline + parse pour chaque nouvelle game
      const addedGames = [];
      for (const matchId of newIds) {
        try {
          const details = await getMatchDetails(matchId, platform);
          if (!details) {
            result.errors++;
            continue;
          }
          const timeline = await getMatchTimeline(matchId, platform);
          const game = parseMatchToApexGame(details, timeline, userSettings.riot_puuid);
          if (!game) {
            result.errors++;
            continue;
          }
          await importGame(game);
          addedGames.push(game);
          result.added++;
        } catch (e) {
          console.warn('[useAutoImport] Failed to import match', matchId, e);
          result.errors++;
        }
      }

      result.addedGames = addedGames;
    } catch (e) {
      console.warn('[useAutoImport] Top-level error:', e);
      result.errors++;
    } finally {
      inFlightRef.current = false;
      setIsImporting(false);
      setLastImportAt(new Date());
      setLastResult(result);
      if (manual) lastManualRef.current = Date.now();
    }

    return result;
  }, [userSettings, existingGames, importGame, queueId]);

  // Tick automatique
  useEffect(() => {
    if (!enabled || !userSettings?.riot_puuid) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Tick imediat optionnel (désactivé : on ne veut pas spammer au mount)
    // performImport();

    intervalRef.current = setInterval(() => {
      performImport({ manual: false });
    }, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, userSettings?.riot_puuid, intervalMs, performImport]);

  const triggerImport = useCallback(() => {
    return performImport({ manual: true });
  }, [performImport]);

  return {
    isImporting,
    lastImportAt,
    lastResult,
    triggerImport
  };
}

export default useAutoImport;
